import { useEffect, useState } from "react";
import { Link, useParams } from "wouter";
import { format } from "date-fns";
import { useLocalePreferences } from "@/lib/locale-preferences";
import { calculateSubtotal, FIXED_FEE_AMOUNT } from "@/lib/pricing";
import {
  Calendar,
  CheckCircle2,
  Globe,
  Instagram,
  Mail,
  MapPin,
  Music,
  Package,
  PenLine,
  Wrench,
  Star,
  X,
  XCircle,
  GitCompareArrows,
  MessageSquare,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { SEOMeta, StructuredData, generateVendorSchema, generateBreadcrumbSchema } from "@/components/seo";


// Transform listing data from snake_case (database) to camelCase (components) - same as explore-all
function transformListing(listing: any) {
  return {
    ...listing,
    imageUrl: listing.images?.[0] || listing.image_url || listing.imageUrl,
    title: listing.title,
    description: listing.description,
    category: listing.category,
    basePrice: (listing.price || listing.base_price || listing.basePrice || 0) + 1.62 + FIXED_FEE_AMOUNT,
    shippingCost: listing.shipping_cost || listing.shippingCost || 0,
    listingType: listing.listing_type || listing.listingType,
    sellerId: listing.seller_id || listing.sellerId,
    sellerName: listing.seller_name || listing.sellerName,
    estimatedDaysMin: listing.estimated_days_min || listing.estimatedDaysMin,
    estimatedDaysMax: listing.estimated_days_max || listing.estimatedDaysMax,
    tags: listing.tags || [],
    stockQuantity: listing.stock_quantity !== undefined ? listing.stock_quantity : listing.stock,
    trackStock: listing.track_stock !== undefined ? listing.track_stock : listing.track_stock,
  };
}
import { Footer } from "@/components/layout/Footer";
import { ModernProductCard } from "@/components/shared/ModernProductCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { NeonButton } from "@/components/ui/neon-button";
import { categoryLabel } from "@/lib/equipment-catalog";
import { isComparedShop, SHOP_COMPARE_CHANGE_EVENT, toggleComparedShop } from "@/lib/shop-compare";
import { useToast } from "@/hooks/use-toast";
import { buildListingPriceInsights } from "@/lib/listing-pricing";
import { MiniRank } from "@/components/rank/RankBadge";

export default function Shop() {
  const params = useParams();
  const shopId = (params as any).id || "0";
  const { toast } = useToast();
  const { user } = useAuth();
  const [isCompared, setIsCompared] = useState(false);
  const [seller, setSeller] = useState<any>(null);
  const [loadingSeller, setLoadingSeller] = useState(true);
  const [listings, setListings] = useState<any[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [printersData, setPrintersData] = useState<any>(null);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviews, setReviews] = useState<any[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const priceInsights = listings.length ? buildListingPriceInsights(listings) : new Map();

  useEffect(() => {
    async function fetchSeller() {
      try {
        setLoadingSeller(true);
        const { data, error } = await supabase
          .from('users')
          .select('id,display_name,shop_name,bio,location,avatar_url,banner_url,accepting_orders,shop_mode,created_at')
          .eq('id', shopId)
          .single();
        if (data && !error) {
          // Transform seller data to match expected format
          setSeller({
            ...data,
            id: data.id,
            user_id: data.id,
            displayName: data.shop_name || data.display_name || data.username,
            shopName: data.shop_name || data.display_name || data.username,
            avatarUrl: data.avatar_url || data.avatar || data.profile_image_url || null,
            accepting_orders: data.accepting_orders ?? true,
            bannerUrl: data.banner_url || data.hero_image_url,
            location: data.location,
            rating: data.rating || 0,
            reviewCount: data.review_count || 0,
            sellerTags: data.seller_tags || [],
            totalPrints: data.total_prints || 0,
            shopMode: data.shop_mode || 'both',
            bio: data.bio,
            joinedAt: data.created_at,
            emailVerifiedAt: data.email_verified_at || (data.is_verified ? data.created_at : null),
            shopAnnouncement: null,
            brandStory: null,
            websiteUrl: null,
            instagramHandle: null,
            supportEmail: null,
            tiktokHandle: null,
            xHandle: null,
            planTier: null,
          });
        }
      } catch (err) {
        console.error('Error fetching seller:', err);
        setSeller(null);
      } finally {
        setLoadingSeller(false);
      }
    }
    fetchSeller();
  }, [shopId]);

  // Fetch listings directly from Supabase - same pattern as explore-all.tsx
  useEffect(() => {
    async function fetchListings() {
      try {
        setLoadingListings(true);
        const { data, error } = await supabase
          .from('listings')
          .select('*')
          .eq('seller_id', shopId);
        if (data && !error) {
          setListings(data.map(transformListing));
        }
      } catch (err) {
        console.error('Error fetching listings:', err);
      } finally {
        setLoadingListings(false);
      }
    }
    fetchListings();
  }, [shopId]);

  // Fetch printers
  useEffect(() => {
    async function fetchPrinters() {
      try {
        setLoadingPrinters(true);
        const { data, error } = await supabase
          .from('printers')
          .select('*')
          .eq('user_id', shopId);
        if (data && !error) {
          setPrintersData(data);
        }
      } catch (err) {
        console.error('Error fetching printers:', err);
      } finally {
        setLoadingPrinters(false);
      }
    }
    fetchPrinters();
  }, [shopId]);

  // Fetch reviews
  useEffect(() => {
    async function fetchReviews() {
      try {
        setLoadingReviews(true);
        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .eq('reviewee_id', shopId);
        if (data && !error) {
          setReviews(data);
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
      } finally {
        setLoadingReviews(false);
      }
    }
    fetchReviews();
  }, [shopId]);

  useEffect(() => {
    const sync = () => setIsCompared(isComparedShop(shopId));
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener(SHOP_COMPARE_CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(SHOP_COMPARE_CHANGE_EVENT, sync);
    };
  }, [shopId]);

  if (loadingSeller) {
    return (
      <div className="min-h-screen flex flex-col">
        <main className="flex-grow container mx-auto px-4 pt-12">
          <Skeleton className="h-64 w-full bg-white/5 rounded-3xl" />
        </main>
      </div>
    );
  }

  if (!seller) {
    return (
      <>
        <SEOMeta
          title="Shop Not Found | Synthix"
          description="The vendor shop you're looking for doesn't exist or has been removed."
          noIndex={true}
        />
        <div className="min-h-screen flex items-center justify-center text-white">Shop not found.</div>
      </>
    );
  }

  // SEO data for vendor shop
  const shopName = seller.shopName || seller.displayName || "Maker Shop";
  const seoTitle = `${shopName} - 3D Printing & Laser Cutting Services | Synthix Marketplace`;
  const seoDescription = seller.bio ? 
    `${seller.bio.slice(0, 160)}${seller.bio.length > 160 ? '...' : ''}` : 
    `Visit ${shopName} on Synthix for custom 3D printing, laser cutting, and maker services.`;
  const canonicalUrl = `https://synthix.com/shop/${shopId}`;
  const bannerUrl = seller.bannerUrl || "https://synthix.com/default-shop-banner.jpg";
  const logoUrl = seller.logoUrl || seller.avatarUrl || "https://synthix.com/default-shop-logo.jpg";
  
  // Generate structured data for vendor
  const vendorSchema = generateVendorSchema({
    name: shopName,
    description: seller.bio || `${shopName} offers 3D printing and maker services on Synthix`,
    image: bannerUrl,
    url: canonicalUrl,
    email: seller.email,
    rating: seller.averageRating ? {
      value: seller.averageRating,
      count: seller.reviewCount || 0,
    } : undefined,
    services: seller.services || ["3D Printing", "Laser Cutting", "Custom Fabrication"],
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "https://synthix.com" },
    { name: "Shops", url: "https://synthix.com/discover" },
    { name: shopName, url: canonicalUrl },
  ]);

  return (
    <>
      <SEOMeta
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        image={bannerUrl}
        type="profile"
        keywords={[
          "3D printing service",
          "laser cutting",
          "custom fabrication",
          shopName,
          "maker services",
          "3D printing shop",
          seller.location || "",
        ].filter(Boolean)}
        author={shopName}
      />
      <StructuredData schema={[vendorSchema, breadcrumbSchema]} />
      
    <div className="min-h-screen flex flex-col bg-zinc-950">
      {/* Not Accepting Orders Banner */}
      {seller.accepting_orders === false && (
        <div className="bg-red-500/20 border-b border-red-500/30 py-3">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-2 text-red-400">
              <XCircle className="w-5 h-5" />
              <span className="font-medium">This shop is currently not accepting orders</span>
            </div>
          </div>
        </div>
      )}

      <main className="flex-grow">
        {/* Full-width Banner */}
        <div className="relative w-full h-64 md:h-80 overflow-hidden">
          {seller.bannerUrl ? (
            <img
              src={seller.bannerUrl}
              alt={`${seller.shopName || seller.displayName} banner`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/20 via-accent/20 to-zinc-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent" />
        </div>

        {/* Profile Section - Overlapping Banner */}
        <div className="container mx-auto px-4 -mt-24 md:-mt-32 relative z-10">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-end mb-8">
            {/* Avatar - Matching Header Style */}
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full p-[3px] bg-gradient-to-br from-primary to-accent shadow-[0_0_30px_rgba(139,92,246,0.4)] shrink-0">
              <div className="w-full h-full rounded-full bg-zinc-900 overflow-hidden">
                {seller.avatarUrl || seller.avatar_url ? (
                  <img 
                    src={seller.avatarUrl || seller.avatar_url} 
                    alt={seller.displayName} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                    <span className="text-3xl md:text-4xl font-bold text-white">
                      {(seller.displayName || "U").charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Shop Info */}
            <div className="flex-grow">
              <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-2">
                {seller.shopName}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400 mb-4">
                {seller.location ? (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-primary" /> {seller.location}
                  </span>
                ) : null}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-accent" /> Joined {format(new Date(seller.joinedAt), "MMM yyyy")}
                </span>
                <span className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-md text-white border border-white/10">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" /> {seller.rating?.toFixed(1) || "New"} ({seller.reviewCount})
                </span>
                {seller.rankId && seller.rankId > 1 && (
                  <MiniRank rankId={seller.rankId} />
                )}
                {seller.emailVerifiedAt ? (
                  <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-200">
                    Verified maker
                  </span>
                ) : null}
                {seller.planTier === "enterprise" ? (
                  <span className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-200">
                    Enterprise
                  </span>
                ) : null}
              </div>
            </div>

            {/* Favorite Button */}
            <div className="flex items-center gap-2 mt-4">
              <FavoriteButton 
                itemId={shopId!}
                itemType="shop"
                className="bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-700"
              />
            </div>

            {/* Action Buttons */}
            <div className="shrink-0 flex gap-3">
              <Button
                variant="outline"
                className="py-3 px-6 rounded-xl glass-panel text-white hover:bg-white/10 border-white/20"
                onClick={() => {
                  const added = toggleComparedShop({
                    id: seller.id,
                    displayName: seller.displayName,
                    shopName: seller.shopName ?? null,
                    location: seller.location ?? null,
                    rating: seller.rating ?? null,
                    reviewCount: seller.reviewCount,
                    shopMode: seller.shopMode ?? null,
                    totalPrints: seller.totalPrints,
                  });
                  toast({
                    title: added ? "Shop added to compare" : "Shop removed from compare",
                    description: added
                      ? "Open compare to review this shop against others."
                      : "This shop is no longer pinned for comparison.",
                  });
                }}
              >
                <GitCompareArrows className="w-5 h-5 mr-2" />
                {isCompared ? "Remove" : "Compare"}
              </Button>
              {seller.accepting_orders !== false && (seller.shopMode === "open" || seller.shopMode === "both") ? (
                <Link href={`/order/new?sellerId=${seller.id}`}>
                  <NeonButton className="py-3 px-6 rounded-xl">Request Custom Work</NeonButton>
                </Link>
              ) : (
                <Button disabled className="py-3 px-6 rounded-xl bg-zinc-700 text-zinc-400 cursor-not-allowed">
                  Not Accepting Orders
                </Button>
              )}
              <Link href="/messages">
                <Button variant="outline" className="py-3 px-6 rounded-xl glass-panel text-white hover:bg-white/10 border-white/20">
                  <MessageSquare className="w-5 h-5 mr-2" /> Contact
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass-panel rounded-2xl p-4 border border-white/10">
              <div className="text-2xl font-bold text-white mb-1">{listings?.length || 0}</div>
              <div className="text-sm text-zinc-400">Products</div>
            </div>
            <div className="glass-panel rounded-2xl p-4 border border-white/10">
              <div className="text-2xl font-bold text-white mb-1">{printersData?.length || 0}</div>
              <div className="text-sm text-zinc-400">Equipment</div>
            </div>
            <div className="glass-panel rounded-2xl p-4 border border-white/10">
              <div className="text-2xl font-bold text-white mb-1">{seller.reviewCount || 0}</div>
              <div className="text-sm text-zinc-400">Reviews</div>
            </div>
            <div className="glass-panel rounded-2xl p-4 border border-white/10">
              <div className="text-2xl font-bold text-white mb-1">{seller.totalPrints || 0}</div>
              <div className="text-sm text-zinc-400">Orders</div>
            </div>
          </div>

          {/* Announcement */}
          {seller.shopAnnouncement ? (
            <div className="mb-8 rounded-2xl border border-primary/20 bg-primary/10 px-6 py-4 text-sm text-white">
              {seller.shopAnnouncement}
            </div>
          ) : null}

          {/* Bio and Badges */}
          <div className="glass-panel rounded-2xl p-6 border border-white/10 mb-8">
            <p className="text-zinc-300 text-lg leading-relaxed mb-4">
              {seller.bio || "Fabrication, additive manufacturing, and custom work - message for details."}
            </p>

            {seller.brandStory ? (
              <p className="text-sm leading-relaxed text-zinc-400 mb-4">
                {seller.brandStory}
              </p>
            ) : null}

            {seller.sellerTags?.length ? (
              <div className="flex flex-wrap gap-2 mb-6">
                {seller.sellerTags.map((tag: string) => (
                  <span key={tag} className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-zinc-200">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            {/* Seller Badges */}
            <div className="flex flex-wrap gap-3">
              {seller.reviewCount >= 3 && (
                <div className="flex items-center gap-2 bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-xl px-4 py-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-400">Trusted Seller</span>
                </div>
              )}
              {printersData && printersData.length >= 3 && (
                <div className="flex items-center gap-2 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl px-4 py-2">
                  <Wrench className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-blue-400">Equipment Expert</span>
                </div>
              )}
              {listings?.length >= 10 && (
                <div className="flex items-center gap-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl px-4 py-2">
                  <Package className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-medium text-purple-400">Pro Catalog</span>
                </div>
              )}
            </div>
          </div>

          {/* Social Media Links */}
          <div className="flex flex-wrap gap-3 mb-8">
            {seller.websiteUrl && (
              <a
                href={seller.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2 transition-colors"
              >
                <Globe className="w-4 h-4 text-zinc-400" />
                <span className="text-sm text-zinc-300">Website</span>
              </a>
            )}
            {seller.instagramHandle && (
              <a
                href={`https://instagram.com/${seller.instagramHandle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2 transition-colors"
              >
                <Instagram className="w-4 h-4 text-zinc-400" />
                <span className="text-sm text-zinc-300">Instagram</span>
              </a>
            )}
            {seller.supportEmail && (
              <a
                href={`mailto:${seller.supportEmail}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2 transition-colors"
              >
                <Mail className="w-4 h-4 text-zinc-400" />
                <span className="text-sm text-zinc-300">Email</span>
              </a>
            )}
            {seller.tiktokHandle && (
              <a
                href={`https://tiktok.com/@${seller.tiktokHandle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2 transition-colors"
              >
                <Music className="w-4 h-4 text-zinc-400" />
                <span className="text-sm text-zinc-300">TikTok</span>
              </a>
            )}
            {seller.xHandle && (
              <a
                href={`https://x.com/${seller.xHandle.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2 transition-colors"
              >
                <X className="w-4 h-4 text-zinc-400" />
                <span className="text-sm text-zinc-300">X</span>
              </a>
            )}
          </div>
        </div>

        {/* Content Tabs - Full Width */}
        <div className="container mx-auto px-4 pb-24">
          <Tabs defaultValue="models" className="w-full">
            <TabsList className="bg-black/40 border border-white/5 p-1 rounded-xl mb-8 w-full justify-start">
              <TabsTrigger value="models" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">Products</TabsTrigger>
              <TabsTrigger value="printers" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">Equipment</TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">Reviews</TabsTrigger>
            </TabsList>

            <TabsContent value="models" className="mt-0">
              {listings?.length === 0 ? (
                <div className="glass-panel p-12 text-center rounded-2xl border border-white/5">
                  <p className="text-zinc-500">This shop doesn't have any catalog models yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {listings?.map((listing) => (
                    <ModernProductCard key={listing.id} listing={listing} priceInsight={priceInsights.get(listing.id)} sellerAcceptingOrders={seller?.accepting_orders} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="printers" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {printersData?.map((printer: any) => (
                  <div key={printer.id} className="glass-panel p-6 rounded-2xl border border-white/5 flex gap-6">
                    <div className="w-24 h-24 rounded-xl bg-black/50 border border-white/10 flex items-center justify-center shrink-0">
                      {printer.imageUrl ? (
                        <img src={printer.imageUrl} alt={printer.name} className="w-full h-full object-cover rounded-xl" />
                      ) : (
                        <Wrench className="w-10 h-10 text-zinc-500" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
                        <h3 className="font-display font-bold text-xl text-white">{printer.name}</h3>
                        <div className="flex flex-wrap gap-1.5 justify-end">
                          <Badge className="bg-white/10 text-zinc-200 border border-white/15">{categoryLabel(printer.equipmentCategory ?? "equipment")}</Badge>
                          <Badge className="bg-accent/20 text-accent border border-accent/30 hover:bg-accent/30">
                            {printer.toolOrServiceType || printer.technology}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-zinc-400 text-sm mb-3">{printer.brand} {printer.model}</p>

                      <div className="space-y-1 text-sm text-zinc-300">
                        <p><span className="text-zinc-500">Capacity:</span> {printer.buildVolume || "-"}</p>
                        <p><span className="text-zinc-500">Capabilities:</span> {printer.materials.join(", ")}</p>
                        {printer.pricePerHour || printer.pricePerGram ? (
                          <p className="text-primary font-medium mt-2">
                            {[
                              printer.pricePerHour != null && `$${printer.pricePerHour}/hr`,
                              printer.pricePerGram != null && `$${printer.pricePerGram}/g`,
                            ].filter(Boolean).join(" · ")}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
                {printersData?.length === 0 ? (
                  <p className="text-zinc-500 col-span-full">No equipment listed publicly.</p>
                ) : null}
              </div>
            </TabsContent>

            <TabsContent value="reviews" className="mt-0">
              <div className="space-y-4">
                {user && user.id !== shopId && (
                  <div className="flex justify-end mb-4">
                    <NeonButton glowColor="accent" onClick={() => setReviewDialogOpen(true)}>
                      <PenLine className="w-4 h-4" />
                      Write a Review
                    </NeonButton>
                  </div>
                )}
                {reviews?.map((review: any) => (
                  <div key={review.id} className="glass-panel p-6 rounded-2xl border border-white/5">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full p-[2px] bg-gradient-to-r from-primary to-accent">
                          <div className="w-full h-full rounded-full bg-zinc-900 overflow-hidden flex items-center justify-center">
                            {review.reviewerAvatarUrl ? (
                              <img src={review.reviewerAvatarUrl} alt={review.reviewerName} className="w-full h-full object-cover" />
                            ) : (
                              <span className="font-bold text-white text-sm">
                                {review.reviewerName.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-white">{review.reviewerName}</p>
                          <p className="text-xs text-zinc-500">{format(new Date(review.createdAt), "MMM d, yyyy")}</p>
                        </div>
                      </div>
                      <div className="flex">
                        {Array.from({ length: 5 }).map((_, index) => (
                          <Star key={index} className={`w-4 h-4 ${index < review.rating ? "text-yellow-400 fill-yellow-400" : "text-zinc-700"}`} />
                        ))}
                      </div>
                    </div>
                    {review.comment ? <p className="text-zinc-300">{review.comment}</p> : null}
                  </div>
                ))}
                {reviews?.length === 0 ? <p className="text-zinc-500">No reviews yet.</p> : null}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Write a Review</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Share your experience with {seller?.shopName || "this seller"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setReviewRating(star)}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= reviewRating
                        ? "text-yellow-400 fill-yellow-400"
                        : "text-zinc-600"
                    }`}
                  />
                </button>
              ))}
            </div>
            <Textarea
              placeholder="Share your experience with this seller..."
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              className="bg-black/30 border-white/10 text-white min-h-[100px]"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setReviewDialogOpen(false)}
                className="border-white/10 bg-white/5 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <NeonButton
                glowColor="accent"
                onClick={async () => {
                  if (!user) return;
                  try {
                    setIsSubmittingReview(true);
                    const { data, error } = await supabase
                      .from('reviews')
                      .insert({
                        reviewer_id: user.id,
                        reviewee_id: shopId,
                        rating: reviewRating,
                        comment: reviewComment,
                      })
                      .select();
                    
                    if (error) throw error;
                    
                    setReviews(prev => [data[0], ...prev]);
                    setReviewDialogOpen(false);
                    setReviewComment("");
                    setReviewRating(5);
                    toast({
                      title: "Review submitted",
                      description: "Thank you for sharing your feedback!",
                    });
                  } catch (error) {
                    console.error('Error submitting review:', error);
                    toast({
                      title: "Error",
                      description: "Failed to submit review. Please try again.",
                      variant: "destructive",
                    });
                  } finally {
                    setIsSubmittingReview(false);
                  }
                }}
                disabled={isSubmittingReview}
              >
                {isSubmittingReview ? "Submitting..." : "Submit Review"}
              </NeonButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
